import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function CustomersList() {
  const [customers, setCustomers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const loadCustomers = () => {
    axios.get('/api/admin/customers').then(res => setCustomers(res.data));
  };

  useEffect(() => { loadCustomers(); }, []);

  const filtered = customers.filter(c =>
    c.name.includes(search) || (c.contact_name && c.contact_name.includes(search))
  );

  const businessCount = customers.filter(c => c.type !== 'residential').length;
  const residentialCount = customers.filter(c => c.type === 'residential').length;

  return (
    <>
      <div className="section-header" style={{ marginBottom: '1.25rem' }}>
        <input
          type="text"
          placeholder="חיפוש לקוח..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginInlineStart: 'auto' }}>
          <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
            <strong style={{ color: '#1B3A5C' }}>{businessCount}</strong> עסקי
            <span style={{ margin: '0 0.5rem', color: '#cbd5e1' }}>•</span>
            <strong style={{ color: '#1B3A5C' }}>{residentialCount}</strong> פרטי
          </span>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ לקוח חדש</button>
        </div>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>שם לקוח</th>
              <th>סוג</th>
              <th>איש קשר</th>
              <th>טלפון</th>
              <th>מייל</th>
              <th>תאריך הצטרפות</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)}>
                <td style={{ fontWeight: 500 }}>
                  {c.name}
                  {c.handoff_state === 'handed_off' && (
                    <span style={{
                      marginInlineStart: '0.5rem', fontSize: '0.68rem', fontWeight: 600,
                      color: '#92400e', background: '#fef3c7', borderRadius: '999px',
                      padding: '0.1rem 0.5rem',
                    }}>נמסר</span>
                  )}
                </td>
                <td>
                  <CustomerTypeBadge type={c.type} />
                </td>
                <td>{c.contact_name || '-'}</td>
                <td>{c.phone || '-'}</td>
                <td>{c.email || '-'}</td>
                <td>{c.created_at ? new Date(c.created_at).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : ''}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                {search ? 'לא נמצאו תוצאות' : 'אין לקוחות עדיין'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <NewCustomerModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); loadCustomers(); }}
        />
      )}
    </>
  );
}

export function CustomerTypeBadge({ type }) {
  const isResidential = type === 'residential';
  const style = {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: '999px',
    padding: '0.15rem 0.6rem',
    color: isResidential ? '#9a3412' : '#1B3A5C',
    background: isResidential ? '#FFEDD5' : '#E0E7FF',
  };
  return <span style={style}>{isResidential ? 'פרטי' : 'עסקי'}</span>;
}

function NewCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    type: 'business',
    name: '', contact_name: '', phone: '', email: '', username: '', password: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post('/api/admin/customers', form);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירת לקוח');
    }
  };

  const isResidential = form.type === 'residential';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>לקוח חדש</h2>
        {error && <div className="login-error">{error}</div>}

        <div className="form-group">
          <label>סוג לקוח</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <CustomerTypeOption
              selected={!isResidential}
              onClick={() => setForm({ ...form, type: 'business' })}
              title="לקוח עסקי"
              description="מפעל / לוגיסטיקה / קניון — עם מפת אתר, קבוצות שערים והרשאות מרובות."
              accentColor="#1B3A5C"
              icon={<BusinessIcon />}
            />
            <CustomerTypeOption
              selected={isResidential}
              onClick={() => setForm({ ...form, type: 'residential' })}
              title="לקוח פרטי"
              description="בית פרטי עם 1–3 שערים. ממשק מצומצם, נעילה הרמטית, בני משפחה."
              accentColor="#F97316"
              icon={<ResidentialIcon />}
            />
          </div>
        </div>

        <div className="form-group">
          <label>שם לקוח</label>
          <input
            value={form.name}
            onChange={e => setForm({...form, name: e.target.value})}
            required
            placeholder={isResidential ? 'משפחת חורי' : 'שערי חורי בע"מ'}
          />
        </div>
        <div className="form-group">
          <label>{isResidential ? 'בעל הבית' : 'איש קשר'}</label>
          <input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} />
        </div>
        <div className="form-group">
          <label>טלפון</label>
          <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
        </div>
        <div className="form-group">
          <label>מייל</label>
          <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
        </div>
        <div className="form-group">
          <label>שם משתמש (לכניסת הלקוח)</label>
          <input value={form.username} onChange={e => setForm({...form, username: e.target.value})} required />
        </div>
        <div className="form-group">
          <label>סיסמה</label>
          <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required />
          <div className="form-hint">לפחות 8 תווים וספרה אחת.</div>
        </div>
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary">צור לקוח</button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

function CustomerTypeOption({ selected, onClick, title, description, accentColor, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        textAlign: 'start',
        padding: '0.9rem',
        background: selected ? '#ffffff' : '#f8fafc',
        border: selected ? `2px solid ${accentColor}` : '2px solid #e2e8f0',
        borderRadius: '12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'all 0.15s',
        boxShadow: selected ? `0 4px 12px ${accentColor}1f` : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <span style={{ color: selected ? accentColor : '#94a3b8' }}>{icon}</span>
        <span style={{ fontWeight: 600, fontSize: '0.95rem', color: selected ? accentColor : '#1B3A5C' }}>
          {title}
        </span>
      </div>
      <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.45 }}>
        {description}
      </div>
    </button>
  );
}

function BusinessIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="13" rx="1" />
      <path d="M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" />
      <line x1="12" y1="12" x2="12" y2="17" />
    </svg>
  );
}

function ResidentialIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

export default CustomersList;
